import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Typography,
    Button,
    Input,
    Textarea,
    Card,
    CardBody,
    IconButton,
    Chip,
    Spinner
} from "@material-tailwind/react";
import {
    PlayIcon,
    StopIcon,
    PlusIcon,
    TrashIcon,
    ClockIcon
} from "@heroicons/react/24/outline";
import ConfirmModal from '../../common/ConfirmModal';

const TaskTimeSection = ({ taskId }) => {
    const { rootUrl } = require('../../../config/config');
    const [token] = useState(localStorage.getItem('authToken') || '');

    const [timeEntries, setTimeEntries] = useState([]);
    const [runningTimer, setRunningTimer] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showManualEntry, setShowManualEntry] = useState(false);
    const [timerElapsed, setTimerElapsed] = useState(0);
    const [deleteEntryId, setDeleteEntryId] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    const [manualEntry, setManualEntry] = useState({
        hours: '',
        minutes: '',
        description: '',
        entry_date: new Date().toISOString().split('T')[0]
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchTimeEntries();
        checkRunningTimer();
    }, [taskId]);

    useEffect(() => {
        let interval;
        if (runningTimer) {
            interval = setInterval(() => {
                const elapsed = Math.floor((new Date() - new Date(runningTimer.start_time)) / 1000);
                setTimerElapsed(elapsed);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [runningTimer]);

    const fetchTimeEntries = async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${rootUrl}/api/tasks/${taskId}/time`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setTimeEntries(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching time entries:', error);
        } finally {
            setLoading(false);
        }
    };

    const checkRunningTimer = async () => {
        try {
            const response = await axios.get(
                `${rootUrl}/api/tasks/${taskId}/time/timer`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success && response.data.data) {
                setRunningTimer(response.data.data);
            }
        } catch (error) {
            console.error('Error checking timer:', error);
        }
    };

    const handleStartTimer = async () => {
        try {
            const response = await axios.post(
                `${rootUrl}/api/tasks/${taskId}/time/start`,
                { user_id: 'current_user' },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setRunningTimer(response.data.data);
            }
        } catch (error) {
            console.error('Error starting timer:', error);
        }
    };

    const handleStopTimer = async () => {
        if (!runningTimer) return;

        try {
            const response = await axios.post(
                `${rootUrl}/api/tasks/time/stop/${runningTimer.entry_id}`,
                { description: 'Timer stopped' },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setRunningTimer(null);
                setTimerElapsed(0);
                fetchTimeEntries();
            }
        } catch (error) {
            console.error('Error stopping timer:', error);
        }
    };

    const handleAddManualEntry = async () => {
        const totalMinutes = (parseInt(manualEntry.hours) || 0) * 60 + (parseInt(manualEntry.minutes) || 0);

        if (totalMinutes <= 0) {
            alert('Please enter a valid time duration');
            return;
        }

        try {
            const response = await axios.post(
                `${rootUrl}/api/tasks/${taskId}/time/manual`,
                {
                    duration_minutes: totalMinutes,
                    description: manualEntry.description,
                    entry_date: manualEntry.entry_date,
                    user_id: 'current_user'
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setTimeEntries([response.data.data, ...timeEntries]);
                setManualEntry({
                    hours: '',
                    minutes: '',
                    description: '',
                    entry_date: new Date().toISOString().split('T')[0]
                });
                setShowManualEntry(false);
            }
        } catch (error) {
            console.error('Error adding manual entry:', error);
        }
    };

    const handleDeleteEntry = async () => {
        if (!deleteEntryId) return;

        setDeleteLoading(true);
        setDeleteError('');

        try {
            const response = await axios.delete(
                `${rootUrl}/api/tasks/time/${deleteEntryId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setTimeEntries(timeEntries.filter(e => e.entry_id !== deleteEntryId));
                setDeleteEntryId(null);
            } else {
                setDeleteError(response.data.message || 'Failed to delete time entry');
            }
        } catch (error) {
            console.error('Error deleting entry:', error);
            setDeleteError(error?.response?.data?.message || 'Failed to delete time entry');
        } finally {
            setDeleteLoading(false);
        }
    };

    const formatDuration = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const formatTime = (minutes) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;

        if (hours === 0) return `${mins}m`;
        if (mins === 0) return `${hours}h`;
        return `${hours}h ${mins}m`;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getTotalTime = () => {
        const total = timeEntries.reduce((sum, entry) => sum + (entry.duration_minutes || 0), 0);
        return formatTime(total);
    };

    return (
        <div className="space-y-6">
            {/* Timer Control */}
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
                <CardBody>
                    <div className="flex items-center justify-between">
                        <div>
                            <Typography variant="h6" color="blue-gray">
                                Time Tracker
                            </Typography>
                            {runningTimer ? (
                                <Typography variant="h3" color="blue" className="font-mono mt-2">
                                    {formatDuration(timerElapsed)}
                                </Typography>
                            ) : (
                                <Typography variant="small" color="gray" className="mt-1">
                                    No timer running
                                </Typography>
                            )}
                        </div>
                        <div className="flex gap-2">
                            {runningTimer ? (
                                <Button
                                    className="flex items-center gap-2 bg-red-500"
                                    onClick={handleStopTimer}
                                >
                                    <StopIcon className="h-5 w-5" />
                                    Stop Timer
                                </Button>
                            ) : (
                                <Button
                                    className="flex items-center gap-2 bg-green-500"
                                    onClick={handleStartTimer}
                                >
                                    <PlayIcon className="h-5 w-5" />
                                    Start Timer
                                </Button>
                            )}
                        </div>
                    </div>
                </CardBody>
            </Card>

            {/* Manual Entry Form */}
            <div>
                {!showManualEntry ? (
                    <Button
                        variant="outlined"
                        className="flex items-center gap-2"
                        onClick={() => setShowManualEntry(true)}
                    >
                        <PlusIcon className="h-4 w-4" />
                        Add Manual Entry
                    </Button>
                ) : (
                    <Card>
                        <CardBody>
                            <Typography variant="h6" color="blue-gray" className="mb-4">
                                Add Manual Time Entry
                            </Typography>
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <Input
                                        type="number"
                                        label="Hours"
                                        value={manualEntry.hours}
                                        onChange={(e) => setManualEntry({ ...manualEntry, hours: e.target.value })}
                                        min="0"
                                    />
                                    <Input
                                        type="number"
                                        label="Minutes"
                                        value={manualEntry.minutes}
                                        onChange={(e) => setManualEntry({ ...manualEntry, minutes: e.target.value })}
                                        min="0"
                                        max="59"
                                    />
                                </div>
                                <Input
                                    type="date"
                                    label="Date"
                                    value={manualEntry.entry_date}
                                    onChange={(e) => setManualEntry({ ...manualEntry, entry_date: e.target.value })}
                                />
                                <Textarea
                                    label="Description (optional)"
                                    value={manualEntry.description}
                                    onChange={(e) => setManualEntry({ ...manualEntry, description: e.target.value })}
                                    rows={2}
                                />
                                <div className="flex gap-2 justify-end">
                                    <Button
                                        variant="text"
                                        onClick={() => {
                                            setShowManualEntry(false);
                                            setManualEntry({
                                                hours: '',
                                                minutes: '',
                                                description: '',
                                                entry_date: new Date().toISOString().split('T')[0]
                                            });
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        className="bg-[#FF5733]"
                                        onClick={handleAddManualEntry}
                                    >
                                        Add Entry
                                    </Button>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                )}
            </div>

            {/* Time Summary */}
            <Card className="bg-gray-50">
                <CardBody>
                    <div className="flex items-center justify-between">
                        <Typography variant="h6" color="blue-gray">
                            Total Time Logged
                        </Typography>
                        <Chip
                            value={getTotalTime()}
                            color="blue"
                            size="lg"
                            icon={<ClockIcon className="h-4 w-4" />}
                        />
                    </div>
                </CardBody>
            </Card>

            {/* Time Entries List */}
            <div>
                <Typography variant="h6" color="blue-gray" className="mb-4">
                    Time Entries
                </Typography>
                {loading ? (
                    <div className="flex justify-center py-8">
                        <Spinner className="h-8 w-8" color="blue" />
                    </div>
                ) : timeEntries.length === 0 ? (
                    <Typography color="gray" className="text-center py-8">
                        No time entries yet
                    </Typography>
                ) : (
                    <div className="space-y-3">
                        {timeEntries.map((entry) => (
                            <Card key={entry.entry_id}>
                                <CardBody className="p-4">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <Chip
                                                    value={formatTime(entry.duration_minutes)}
                                                    size="sm"
                                                    color="blue"
                                                />
                                                <Typography variant="small" color="gray">
                                                    {formatDate(entry.start_time || entry.entry_date)}
                                                </Typography>
                                                {entry.is_timer && (
                                                    <Chip value="Timer" size="sm" color="green" />
                                                )}
                                            </div>
                                            {entry.description && (
                                                <Typography variant="small" className="text-gray-700">
                                                    {entry.description}
                                                </Typography>
                                            )}
                                            <Typography variant="small" color="gray" className="mt-1">
                                                By {entry.user_name || 'Unknown User'}
                                            </Typography>
                                        </div>
                                        <IconButton
                                            variant="text"
                                            color="red"
                                            size="sm"
                                            onClick={() => setDeleteEntryId(entry.entry_id)}
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </IconButton>
                                    </div>
                                </CardBody>
                            </Card>
                        ))}
                    </div>
                )}

                <ConfirmModal
                    isOpen={Boolean(deleteEntryId)}
                    title="Delete time entry"
                    description="This entry will be permanently removed from the task."
                    onCancel={() => {
                        setDeleteEntryId(null);
                        setDeleteError('');
                    }}
                    onConfirm={handleDeleteEntry}
                    confirmText="Delete"
                    cancelText="Cancel"
                    loading={deleteLoading}
                    error={deleteError}
                    variant="danger"
                />
            </div>
        </div>
    );
};

export default TaskTimeSection;
